import Buttons from "./Buttons";
import "./PaymentsTab.css";
import { useEffect, useState, useMemo } from "react"; // 🆕 added useMemo
import { PaymentsService, ParentLimitedResponse } from "client/openapi";
import { ErrorBlock, LoadingBlock } from "components/StatusBlock";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { APIResponse, PageStatus } from "types";
import Notifications from "util/notifications";

function Payments({ parent }: { parent: ParentLimitedResponse }) {
  const [clientSecret, setClientSecret] = useState<string>();
  const [stripeAccountId, setStripeAccountId] = useState<string>();
  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADING);
  const [error, setError] = useState<APIResponse>();

  let currentDate = new Date(); // Get the current date and time
  let pastDate = new Date(currentDate); // Create a new Date object based on the current date
  pastDate.setMonth(pastDate.getMonth() - 6);

  useEffect(() => {
    const getClientSecretForSetupIntent = async () => {
      setStatus(PageStatus.LOADING);
      try {
        const setupResponse = await PaymentsService.addPaymentMethodParent({
          parentId: parent.id,
        });
        setStatus(PageStatus.SUCCESS);
        setStripeAccountId(setupResponse.stripe_account_id || "");
        setClientSecret(setupResponse.client_secret || "");
      } catch (e: any) {
        setStatus(PageStatus.ERROR);
        setError({ error: "Could not get student payment details." });
        console.error(`Error (#${e.status}): ${e.message}`);
      }
    };

    getClientSecretForSetupIntent();
  }, [parent.id]);

  /* Build Stripe once the SetupIntent is made, scoped to the account it is on:
     the org's connected account, or Tadpole's platform account (no account)
     for a customer made before the org had one. */
  const stripePromise = useMemo(() => {
    if (!clientSecret) return null;
    return loadStripe(
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TADPOLE_BILLING as string,
      stripeAccountId ? { stripeAccount: stripeAccountId } : undefined
    );
  }, [clientSecret, stripeAccountId]);

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  /* Wait until both async values are ready */
  if (!clientSecret || !stripePromise) {
    if (status === PageStatus.LOADING) {
      return <LoadingBlock status={status} />;
    }
    if (status === PageStatus.ERROR) {
      return <ErrorBlock status={status} message={error?.message} />;
    }
    return null;
  }

  return (
    // Elements cannot change its Stripe or client secret once set, so it starts
    // over when another person's SetupIntent arrives.
    <Elements
      key={clientSecret}
      stripe={stripePromise}
      options={{ clientSecret }}
    >
      <div>
        <div className="flex flex-col lg:flex-row">
          <div className="mt-5 lg:mt-0">
            <Buttons parent={parent} orgStripeAccountId={stripeAccountId} />
          </div>
        </div>
      </div>
    </Elements>
  );
}
export default Payments;
